import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react'
import type { ReactNode } from 'react'
import { api, auth } from '../api/client'

/**
 * Per-user UI state, persisted server-side so it follows the user between
 * browsers rather than living in one machine's localStorage.
 *
 * <p>Writes are debounced and coalesced: collapsing four cards in quick
 * succession is one PUT, not four. A failed save is logged and dropped rather
 * than surfaced — losing a collapse state is not worth an error banner on a
 * monitoring dashboard, and the next successful write repairs it.
 *
 * <p>The last known state is mirrored to localStorage purely so a reload
 * renders the right layout immediately instead of flashing the default while
 * the fetch is in flight.
 */

type Prefs = Record<string, unknown>

interface PreferencesApi {
  ready: boolean
  get<T>(key: string, fallback: T): T
  set(key: string, value: unknown): void
}

const CACHE_KEY = 'tmmon.prefs.cache'
const SAVE_DEBOUNCE_MS = 800

const PreferencesContext = createContext<PreferencesApi>({
  ready: false,
  get: (_k, fallback) => fallback,
  set: () => {},
})

function readCache(): Prefs {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? (JSON.parse(raw) as Prefs) : {}
  } catch {
    return {}
  }
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<Prefs>(readCache)
  const [ready, setReady] = useState(false)

  // Held in a ref as well as state so the debounced save always writes the
  // newest value rather than whatever was current when the timer was armed.
  const latest = useRef<Prefs>(prefs)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    if (!auth.isValid()) {
      setReady(true)
      return
    }
    api.preferences()
      .then((server) => {
        if (cancelled) return
        latest.current = server
        setPrefs(server)
        localStorage.setItem(CACHE_KEY, JSON.stringify(server))
      })
      .catch((e) => console.warn('Could not load preferences:', e))
      .finally(() => { if (!cancelled) setReady(true) })
    return () => { cancelled = true }
  }, [])

  const flush = useCallback(() => {
    const payload = latest.current
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
    if (!auth.isValid()) return
    api.savePreferences(payload).catch((e) =>
      console.warn('Could not save preferences:', e))
  }, [])

  // Persist anything still pending when the tab goes away.
  useEffect(() => {
    const onHide = () => {
      if (timer.current !== undefined) {
        window.clearTimeout(timer.current)
        timer.current = undefined
        flush()
      }
    }
    window.addEventListener('pagehide', onHide)
    document.addEventListener('visibilitychange', onHide)
    return () => {
      window.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onHide)
    }
  }, [flush])

  const set = useCallback((key: string, value: unknown) => {
    const next = { ...latest.current, [key]: value }
    latest.current = next
    setPrefs(next)

    if (timer.current !== undefined) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      timer.current = undefined
      flush()
    }, SAVE_DEBOUNCE_MS)
  }, [flush])

  const value = useMemo<PreferencesApi>(() => ({
    ready,
    get: <T,>(key: string, fallback: T): T =>
      (prefs[key] === undefined ? fallback : (prefs[key] as T)),
    set,
  }), [prefs, ready, set])

  return (
    <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
  )
}

export function usePreferences(): PreferencesApi {
  return useContext(PreferencesContext)
}

/** One preference value, with the same ergonomics as useState. */
export function usePreference<T>(key: string, fallback: T): [T, (v: T) => void] {
  const prefs = usePreferences()
  const value = prefs.get<T>(key, fallback)
  const setValue = useCallback((v: T) => prefs.set(key, v), [prefs, key])
  return [value, setValue]
}
